import React, { useState, useMemo } from 'react';
import {
  Box,
  Popper,
  ListItem,
  ListItemText,
  ClickAwayListener,
  InputBase,
  ButtonBase,
  Typography,
  IconButton,
  createFilterOptions
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import MuiLink from '@mui/material/Link';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
const filter = createFilterOptions();
const ADD_PREFIX = 'Add "';

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    backgroundColor: 'transparent',
    backgroundImage: 'none'
  },
  [`& .${autocompleteClasses.listbox}`]: {
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent'
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
        backgroundColor: theme.palette.selectedBackground.selector
      }
    }
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative'
  }
}));

function PopperComponent(props) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.neutral[900], 0.2)}`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: '10px 16px',
  width: '100%',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& input': {
    borderRadius: 4,
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.divider}`,

    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.mode === 'light' ? theme.palette.neutral[700] : theme.palette.blue.link,
      boxShadow: `${theme.palette.mode === 'light' ? theme.palette.neutral[700] : theme.palette.blue.link} 0 0 0 1px inset`
    }
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: 0,
    marginRight: 8
  }
}));

const CustomButton = styled(ButtonBase)(({ theme }) => ({
  padding: `8.5px 6px 8.5px ${theme.spacing(1.75)}`,
  justifyContent: 'flex-start',
  borderRadius: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  height: theme.spacing(5),
  width: '100%',
  textAlign: 'left',
  paddingRight: '50px',
  border: `1px solid ${theme.palette.input.border}`,

  '&:hover': {
    borderColor: theme.palette.text.primary
  },

  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.mode === 'light' ? theme.palette.neutral[700] : theme.palette.blue.link,
    boxShadow: `${theme.palette.mode === 'light' ? theme.palette.neutral[700] : theme.palette.blue.link} 0 0 0 1px inset`
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.selectedBackground.selector
  }
}));

const NRAutocompleteFilter = ({
  value,
  setValue,
  options,
  placeholder = 'Select...',
  filterPlaceholder = 'Filter',
  showMultipleLicensesWarning = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [invalidLicense, setInvalidLicense] = useState(null);
  const theme = useTheme();
  const sortedOptions = useMemo(() => {
    if (!Boolean(anchorEl)) {
      return options;
    }

    const selectedOption = options.filter(option => value === option);
    const unselectedOptions = options.filter(option => value !== option);

    return [...selectedOption, ...unselectedOptions].filter(Boolean);
  }, [options, value, anchorEl]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (anchorEl) anchorEl.focus();
    setAnchorEl(null);
  };

  const handleOnClear = e => {
    e.stopPropagation();
    setValue(null);
  };

  const isInputInvalid = v => {
    if (!v || typeof v !== 'string') {
      // No value (or non-string) should not trigger the invalid warning
      return false;
    }

    const operators = new Set(['AND', 'OR', 'WITH']);
    const optionSet = new Set(options);

    // 1) Replace parentheses with spaces
    const normalized = v.replace(/[()]/g, ' ');

    // 2) Trim, then split on whitespace
    const tokens = normalized.trim().split(/\s+/).filter(Boolean);

    if (tokens.length === 0) {
      return false;
    }

    // 3) Every token must be either a valid option or a valid operator
    const isValid = tokens.every(token => optionSet.has(token) || operators.has(token));

    // Function returns "invalid" flag
    return !isValid;
  };

  const handleSetValue = v => {
    setValue(v);
    !!v && setInvalidLicense(isInputInvalid(v));
  };

  const open = Boolean(anchorEl);
  const id = open ? `autocomplete-popper` : undefined;

  return (
    <>
      <Box sx={{ width: '100%', fontSize: 16 }}>
        <CustomButton aria-describedby={id} onClick={handleClick} disabled={!options.length} disableTouchRipple>
          {!!value ? (
            <Typography variant="body1" noWrap>
              {value}
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              {placeholder}
            </Typography>
          )}

          {!!value && (
            <IconButton size="small" sx={{ position: 'absolute', right: 30 }} onClick={handleOnClear}>
              <ClearIcon />
            </IconButton>
          )}
          <ArrowDropDown
            sx={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: theme.palette.text.iconSecondary,
              ...(open && { transform: 'translateY(-50%) rotate(180deg)' })
            }}
          />
        </CustomButton>
        {showMultipleLicensesWarning && (
          <Box sx={{ mt: 0.5, ml: 2 }} gap={1} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <WarningAmberOutlinedIcon
              sx={{ width: theme.spacing(2), height: theme.spacing(2), color: theme.palette.warning.main, mt: theme.spacing(0.25) }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Warning: Selecting a new license from this list will overwrite and replace existing ones.
            </Typography>
          </Box>
        )}
        {!!value && invalidLicense && (
          <Box sx={{ mt: 0.5, ml: 2 }} gap={1} display="flex" flexDirection="row">
            <WarningAmberOutlinedIcon
              sx={{ width: theme.spacing(2), height: theme.spacing(2), color: theme.palette.warning.main, mt: theme.spacing(0.5) }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                This license or expression is not valid. Submission is allowed, but it should follow the guidelines below:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 0.25, mb: 0 }}>
                <Typography component="li" variant="caption" color="text.secondary">
                  Use SPDX-compliant identifiers from the
                  <MuiLink
                    href="https://spdx.org/licenses/"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      color: theme.palette.blue.link,
                      textDecoration: 'none',
                      '&:visited': {
                        color: theme.palette.blue.link
                      }
                    }}
                  >
                    <Typography component="span" sx={{ pl: 0.5 }} variant="caption">
                      official list
                    </Typography>
                  </MuiLink>
                  .
                </Typography>
                <Typography component="li" variant="caption" color="text.secondary">
                  Operators must be AND, OR, or WITH.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {!!options.length && (
        <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" style={{ width: anchorEl?.offsetWidth }}>
          <ClickAwayListener onClickAway={handleClose}>
            <Box>
              <Autocomplete
                open
                multiple={false}
                value={value}
                onClose={(event, reason) => {
                  if (reason === 'escape') handleClose();
                }}
                onChange={(event, newValue) => {
                  let valueToUse = newValue;
                  if (newValue?.includes(ADD_PREFIX)) {
                    valueToUse = newValue.replace(ADD_PREFIX, '').replace('"', '');
                  }
                  handleSetValue(valueToUse);
                  handleClose();
                }}
                disableCloseOnSelect
                disableListWrap
                options={sortedOptions}
                PopperComponent={PopperComponent}
                renderTags={() => null}
                noOptionsText="No options"
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some(option => inputValue === option);
                  if (inputValue !== '' && !isExisting) {
                    filtered.push(`${ADD_PREFIX}${inputValue}"`);
                  }

                  return filtered;
                }}
                isOptionEqualToValue={(option, val) => option === val}
                renderOption={(props, option) => {
                  const isSelected = value === option;
                  return (
                    <StyledListItem {...props} key={option}>
                      <Box
                        component={DoneIcon}
                        sx={{
                          width: 17,
                          height: 17,
                          margin: '7px 5px -2px'
                        }}
                        style={{
                          visibility: isSelected ? 'visible' : 'hidden'
                        }}
                      />
                      <Box
                        sx={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          flexGrow: 1,
                          '& p': {
                            color: theme.palette.text.primary
                          }
                        }}
                      >
                        <ListItemText primary={option} />
                      </Box>
                    </StyledListItem>
                  );
                }}
                renderInput={params => (
                  <StyledInput ref={params.InputProps.ref} inputProps={params.inputProps} autoFocus placeholder={filterPlaceholder} />
                )}
              />
            </Box>
          </ClickAwayListener>
        </StyledPopper>
      )}
    </>
  );
};

export default NRAutocompleteFilter;
